/**
 * Nudges from GET /api/users/current: user.nudges with entries like
 *   "bayut-match" | "bayut_match" → { count, expires_at, window_hours? }
 *   optional "lms_intro" / "lms-intro" → { expires_at, window_hours? }
 * expires_at: ISO-8601 preferred, or "YYYY-MM-DD HH:mm:ss" (parsed as local).
 */
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { cookieDomain, TENANT_KEY } from './env';

dayjs.extend(customParseFormat);

export const NUDGE_KEY_BAYUT_MATCH = 'bayut_match';
const COOKIE_SAME_BASE = { path: '/', sameSite: 'Lax' };

const getBaseCookieOptions = () => {
  if (typeof window === 'undefined') {
    return { ...COOKIE_SAME_BASE };
  }
  return {
    ...COOKIE_SAME_BASE,
    secure: window.location?.protocol === 'https:',
    ...(cookieDomain && { domain: cookieDomain }),
  };
};

const BAYUT_MATCH_NUDGE_KEYS = ['bayut-match', 'bayut_match', 'bayutMatch'];
const LMS_INTRO_NUDGE_KEYS = ['lms_intro', 'lms-intro', 'lmsIntro'];

const pickFirstNudge = (raw, keyCandidates) => {
  for (const k of keyCandidates) {
    if (raw[k] != null) return raw[k];
  }
  return null;
};

/**
 * @param {string|Date|number} value
 * @returns {Date|null}
 */
export const parseNudgeExpiresAt = (value) => {
  if (value == null) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const s = String(value).trim();
  if (!s) return null;
  const isoTry = dayjs(s);
  if (isoTry.isValid()) return isoTry.toDate();
  const withT = s.includes('T') ? dayjs(s) : dayjs(s.replace(' ', 'T'));
  if (withT.isValid()) return withT.toDate();
  const p = dayjs(s, 'YYYY-MM-DD HH:mm:ss', true);
  if (p.isValid()) return p.toDate();
  return null;
};

const parseRedirectionUrls = (entry) => {
  const raw = entry?.redirection_url ?? entry?.redirectionUrl;
  if (!raw || typeof raw !== 'object') return null;
  const en = typeof raw.en === 'string' ? raw.en : null;
  const ar = typeof raw.ar === 'string' ? raw.ar : null;
  if (!en && !ar) return null;
  return { en, ar };
};

/**
 * @param {{ redirectionUrls?: { en?: string|null, ar?: string|null }|null }|null} nudge
 * @param {string} [locale] i18n language, e.g. en, ar
 * @returns {string|null}
 */
export const resolveNudgeRedirectionUrl = (nudge, locale) => {
  const m = nudge?.redirectionUrls;
  if (!m) return null;
  const lang = String(locale || 'en').split(/[-_]/)[0].toLowerCase();
  if (lang === 'ar' && m.ar) return m.ar;
  if (m[lang]) return m[lang];
  return m.en || m.ar || null;
};

const attachRedirection = (entry, base) => {
  const redirectionUrls = parseRedirectionUrls(entry);
  return {
    ...base,
    redirectionUrls,
    getRedirectionUrl: (locale) => resolveNudgeRedirectionUrl({ redirectionUrls }, locale),
  };
};

const normalizeNudgeBlock = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  const count = Number(entry.count) || 0;
  const expiresAt = parseNudgeExpiresAt(entry.expires_at ?? entry.expiresAt);
  const windowHours =
    entry.window_hours != null
      ? Number(entry.window_hours)
      : entry.windowHours != null
        ? Number(entry.windowHours)
        : null;
  if (!expiresAt) {
    return attachRedirection(entry, {
      count,
      expiresAt: null,
      windowHours: Number.isFinite(windowHours) ? windowHours : null,
    });
  }
  return attachRedirection(entry, {
    count,
    expiresAt,
    windowHours: Number.isFinite(windowHours) ? windowHours : null,
  });
};

/**
 * @param {object|null|undefined} raw
 * @returns {{ bayutMatch: { count: number, expiresAt: Date|null, windowHours: number|null, redirectionUrls?: object, getRedirectionUrl?: (locale: string) => string|null }|null, lmsIntro: { expiresAt: Date|null, windowHours: number|null }|null }}
 */
export const parseUserNudges = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return { bayutMatch: null, lmsIntro: null };
  }
  return {
    bayutMatch: normalizeNudgeBlock(pickFirstNudge(raw, BAYUT_MATCH_NUDGE_KEYS)),
    lmsIntro: (() => {
      const b = pickFirstNudge(raw, LMS_INTRO_NUDGE_KEYS);
      if (!b) return null;
      const m = normalizeNudgeBlock(b);
      if (!m) return null;
      return { expiresAt: m.expiresAt, windowHours: m.windowHours };
    })(),
  };
};

const DEFAULT_LMS_INTRO_FALLBACK_HOURS = 24;
const LEGACY_MIGRATION_YEARS = 1;

const resolveLmsIntroExpires = (user) => {
  const p = user?.parsedNudges;
  const fromLms = p?.lmsIntro?.expiresAt;
  const fromMatch = p?.bayutMatch?.expiresAt;
  for (const d of [fromLms, fromMatch]) {
    if (d && d.getTime() > Date.now()) return d;
  }
  return dayjs()
    .add(
      p?.lmsIntro?.windowHours != null
        ? p.lmsIntro.windowHours
        : p?.bayutMatch?.windowHours != null
          ? p.bayutMatch.windowHours
          : DEFAULT_LMS_INTRO_FALLBACK_HOURS,
      'hour',
    )
    .toDate();
};

const sanitizeKeySegment = (k) => String(k).replace(/[^a-zA-Z0-9_]/g, '_');

/**
 * @param {number|string} userId
 * @param {string} nudgeKey
 */
export const getNudgeCookieName = (userId, nudgeKey) =>
  `nudge_${TENANT_KEY}_${sanitizeKeySegment(nudgeKey)}_${userId}`;

export const getLmsIntroCookieName = (userId) => `lms_intro_${TENANT_KEY}_${userId}`;

export const getLmsIntroMigratedKey = (userId) => `lmsIntroCookieMigrated_${userId}`;

const buildBayutNudgeToken = (nudge) => {
  const t = nudge?.expiresAt instanceof Date && !Number.isNaN(nudge.expiresAt.getTime()) ? nudge.expiresAt.getTime() : 0;
  return `${nudge?.count ?? 0}@${t}`;
};

/**
 * @param {number|string} userId
 * @param {string} nudgeKey
 * @param {{ count: number, expiresAt: Date|null, windowHours?: number|null }|null} nudge
 */
export const isBayutMatchNudgeSuppressed = (userId, nudgeKey, nudge) => {
  if (!userId || !nudge?.expiresAt) return true;
  const name = getNudgeCookieName(userId, nudgeKey);
  const v = Cookies.get(name);
  return v === buildBayutNudgeToken(nudge);
};

/**
 * @param {number|string} userId
 * @param {string} nudgeKey
 * @param {{ count: number, expiresAt: Date|null, windowHours?: number|null }|null} nudge
 */
export const suppressNudgeForPeriod = (userId, nudgeKey, nudge) => {
  if (!userId || !nudge?.expiresAt) return;
  const t = buildBayutNudgeToken(nudge);
  if (!nudge.expiresAt || nudge.expiresAt.getTime() <= Date.now()) return;
  const name = getNudgeCookieName(userId, nudgeKey);
  Cookies.set(name, String(t), { ...getBaseCookieOptions(), expires: nudge.expiresAt });
};

export const isLmsIntroSuppressed = (userId) => {
  if (!userId) return false;
  return !!Cookies.get(getLmsIntroCookieName(userId));
};

export const lmsIntroCookieMigrated = (userId) =>
  typeof localStorage !== 'undefined' && localStorage.getItem(getLmsIntroMigratedKey(userId)) === '1';

/**
 * One-time: users who only had tapTargets (no lms intro cookie) get a long cookie so the first load does not flash the modal.
 * @param {number|string} userId
 * @returns {boolean} true if a migration cookie was written
 */
export const legacyMigrateLmsIntroForPreCookieUsers = (userId) => {
  if (!userId) return false;
  if (lmsIntroCookieMigrated(userId) || isLmsIntroSuppressed(userId)) return false;
  if (typeof localStorage === 'undefined') return false;
  const raw = localStorage.getItem('tapTargets');
  if (!raw) return false;
  let tap;
  try {
    tap = JSON.parse(raw);
  } catch {
    return false;
  }
  if (!tap?.lms?.introModal?.hide) return false;
  const exp = dayjs().add(LEGACY_MIGRATION_YEARS, 'year').toDate();
  const name = getLmsIntroCookieName(userId);
  Cookies.set(name, '1', { ...getBaseCookieOptions(), expires: exp });
  localStorage.setItem(getLmsIntroMigratedKey(userId), '1');
  return true;
};

const clearLmsIntroTapTargetsHide = () => {
  if (typeof localStorage === 'undefined') return;
  const raw = localStorage.getItem('tapTargets');
  if (!raw) return;
  try {
    const tap = JSON.parse(raw);
    if (!tap?.lms) return;
    const next = {
      ...tap,
      lms: { ...tap.lms, introModal: { ...tap.lms?.introModal, hide: false } },
    };
    localStorage.setItem('tapTargets', JSON.stringify(next));
  } catch {
    /* ignore */
  }
};

/**
 * After the LMS intro cookie expires, clear tapTargets hide so the next server period can show the intro again.
 * @param {number|string} userId
 * @returns {boolean} true if tapTargets was updated
 */
export const reEnableLmsIntroInTapTargets = (userId) => {
  if (!userId) return false;
  if (typeof localStorage === 'undefined') return false;
  if (isLmsIntroSuppressed(userId)) return false;
  if (!lmsIntroCookieMigrated(userId)) return false;
  const raw = localStorage.getItem('tapTargets');
  if (!raw) return false;
  let tap;
  try {
    tap = JSON.parse(raw);
  } catch {
    return false;
  }
  if (!tap?.lms?.introModal?.hide) return false;
  clearLmsIntroTapTargetsHide();
  return true;
};

/**
 * @param {number|string} [userId]
 */
export const clearNudgeCookiesForUser = (userId) => {
  if (userId == null || userId === '') return;
  const opts = getBaseCookieOptions();
  const names = [getLmsIntroCookieName(userId), getNudgeCookieName(userId, NUDGE_KEY_BAYUT_MATCH)];
  for (const name of names) {
    Cookies.remove(name, { path: COOKIE_SAME_BASE.path, ...(opts.domain && { domain: opts.domain }) });
  }
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(getLmsIntroMigratedKey(userId));
    } catch {
      /* ignore */
    }
  }
};

/**
 * Dismissal expiry for the LMS/TruLeads intro: prefer nudge `expires_at`, else 24h from now.
 * @param {object} [user] login user with optional parsedNudges
 * @returns {Date}
 */
export const resolveLmsIntroDismissExpiresAt = (user) => resolveLmsIntroExpires(user);

/**
 * @param {number|string} userId
 * @param {Date} expiresAt
 */
export const suppressLmsIntro = (userId, expiresAt) => {
  if (!userId) return;
  if (!expiresAt || !(expiresAt instanceof Date) || Number.isNaN(expiresAt.getTime())) return;
  if (expiresAt.getTime() <= Date.now()) return;
  const name = getLmsIntroCookieName(userId);
  Cookies.set(name, '1', { ...getBaseCookieOptions(), expires: expiresAt });
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(getLmsIntroMigratedKey(userId), '1');
  }
};
